import { useState, useMemo } from "react";
import { toast } from "sonner";
import { 
  Plus, Trash2, Edit2, Save, X, Tv, Film, Folder, 
  ChevronRight, ChevronDown, GripVertical, Palette,
  Search, BarChart3, Youtube, Eye, RefreshCw, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ImageUploader from "./ImageUploader";
import adminApi from "@/lib/adminApi";

// Platform icons for view sources
const PLATFORM_ICONS = {
  youtube: <Youtube size={14} className="text-red-500" />,
  facebook: <span className="text-blue-600 font-bold text-xs">f</span>,
  tiktok: <span className="font-bold text-xs">TT</span>
};

// Format view count
const formatViews = (count) => {
  if (!count) return "0";
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toLocaleString();
};

// Format date relative
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "Nunca";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Hace un momento";
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  return `Hace ${diffDays}d`;
};

// Preset colors for seasons
const PRESET_COLORS = [
  { name: "Amarillo", value: "#FFE500" },
  { name: "Rosa", value: "#E91E8C" },
  { name: "Azul", value: "#3B82F6" },
  { name: "Verde", value: "#22C55E" },
  { name: "Rojo", value: "#EF4444" },
  { name: "Morado", value: "#8B5CF6" },
  { name: "Naranja", value: "#F97316" },
  { name: "Cyan", value: "#06B6D4" },
];

// Helper to get contrast color
const getContrastColor = (hexColor) => {
  if (!hexColor) return "#000000";
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

export default function ContentManagerTab({ shows, seasons, videos, onRefresh }) {
  // Expansion state
  const [expandedShows, setExpandedShows] = useState(new Set());
  const [expandedSeasons, setExpandedSeasons] = useState(new Set());
  
  // Edit mode state
  const [editingItem, setEditingItem] = useState(null); // { type: 'show'|'season'|'video', data: {...} }
  const [creatingItem, setCreatingItem] = useState(null); // { type: 'show'|'season'|'video', parentId?: string }
  
  // Search
  const [searchQuery, setSearchQuery] = useState("");

  // Drag & Drop state
  const [draggedItem, setDraggedItem] = useState(null); // { type: 'show'|'season'|'video', id: string, parentId?: string }
  const [dragOverItem, setDragOverItem] = useState(null); // { type: 'show'|'season'|'video', id: string, parentId?: string }

  // Views refresh state
  const [refreshingViews, setRefreshingViews] = useState(false);
  const [viewsData, setViewsData] = useState(null); // { total_views, views_last_updated }

  // Form states
  const [showForm, setShowForm] = useState({ title: "", description: "", thumbnail: "", order: 0 });
  const [seasonForm, setSeasonForm] = useState({ title: "", description: "", thumbnail: "", show_id: "", color: "#FFE500", order: 0 });
  const [videoForm, setVideoForm] = useState({ 
    title: "", description: "", thumbnail: "", video_url: "", platform: "youtube", 
    season_id: "", order: 0, duration: "", view_sources: [] 
  });

  // Sort helpers
  const sortedShows = useMemo(() => 
    [...shows].sort((a, b) => (a.order || 0) - (b.order || 0)), 
    [shows]
  );

  const getSeasonsByShow = (showId) => 
    seasons.filter(s => s.show_id === showId).sort((a, b) => (a.order || 0) - (b.order || 0));

  const getVideosBySeason = (seasonId) => 
    videos.filter(v => v.season_id === seasonId).sort((a, b) => (a.order || 0) - (b.order || 0));

  // Filter by search
  const filteredShows = useMemo(() => {
    if (!searchQuery.trim()) return sortedShows;
    const query = searchQuery.toLowerCase();
    return sortedShows.filter(show => {
      // Match show title
      if (show.title.toLowerCase().includes(query)) return true;
      // Match any season title
      const showSeasons = getSeasonsByShow(show.id);
      if (showSeasons.some(s => s.title.toLowerCase().includes(query))) return true;
      // Match any video title
      const showVideos = showSeasons.flatMap(s => getVideosBySeason(s.id));
      return showVideos.some(v => v.title.toLowerCase().includes(query));
    });
  }, [sortedShows, searchQuery, seasons, videos]);

  // Toggle expansion
  const toggleShow = (showId) => {
    setExpandedShows(prev => {
      const next = new Set(prev);
      if (next.has(showId)) next.delete(showId);
      else next.add(showId);
      return next;
    });
  };

  const toggleSeason = (seasonId) => {
    setExpandedSeasons(prev => {
      const next = new Set(prev);
      if (next.has(seasonId)) next.delete(seasonId);
      else next.add(seasonId);
      return next;
    });
  };

  // Drag & Drop Handlers
  const handleDragStart = (e, type, id, parentId = null) => {
    setDraggedItem({ type, id, parentId });
    e.dataTransfer.effectAllowed = "move";
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      e.target.style.opacity = "0.5";
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragOver = (e, type, id, parentId = null) => {
    e.preventDefault();
    // Only allow dropping on same type and same parent
    if (draggedItem && draggedItem.type === type && draggedItem.parentId === parentId && draggedItem.id !== id) {
      setDragOverItem({ type, id, parentId });
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = async (e, type, dropId, parentId = null) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== type || draggedItem.parentId !== parentId || draggedItem.id === dropId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    try {
      let items, endpoint;
      
      if (type === 'show') {
        items = [...sortedShows];
        endpoint = '/shows';
      } else if (type === 'season') {
        items = getSeasonsByShow(parentId);
        endpoint = '/seasons';
      } else if (type === 'video') {
        items = getVideosBySeason(parentId);
        endpoint = '/videos';
      }

      const dragIndex = items.findIndex(item => item.id === draggedItem.id);
      const dropIndex = items.findIndex(item => item.id === dropId);

      if (dragIndex === -1 || dropIndex === -1) return;

      // Reorder array
      const [movedItem] = items.splice(dragIndex, 1);
      items.splice(dropIndex, 0, movedItem);

      // Update order for all items
      await Promise.all(
        items.map((item, index) => 
          adminApi.put(`${endpoint}/${item.id}`, { order: index })
        )
      );

      toast.success("Orden actualizado");
      onRefresh();
    } catch (error) {
      console.error("Error reordering:", error);
      toast.error("Error al reordenar");
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  // Reset forms
  const resetForms = () => {
    setEditingItem(null);
    setCreatingItem(null);
    setShowForm({ title: "", description: "", thumbnail: "", order: 0 });
    setSeasonForm({ title: "", description: "", thumbnail: "", show_id: "", color: "#FFE500", order: 0 });
    setVideoForm({ 
      title: "", description: "", thumbnail: "", video_url: "", platform: "youtube", 
      season_id: "", order: 0, duration: "", view_sources: [] 
    });
    setViewsData(null);
  };

  // Start creating
  const startCreateShow = () => {
    resetForms();
    setCreatingItem({ type: 'show' });
    setShowForm({ title: "", description: "", thumbnail: "", order: sortedShows.length });
  };

  const startCreateSeason = (showId) => {
    resetForms();
    const showSeasons = getSeasonsByShow(showId);
    setCreatingItem({ type: 'season', parentId: showId });
    setSeasonForm({ title: "", description: "", thumbnail: "", show_id: showId, color: "#FFE500", order: showSeasons.length });
    setExpandedShows(prev => new Set(prev).add(showId));
  };

  const startCreateVideo = (seasonId) => {
    resetForms();
    const seasonVideos = getVideosBySeason(seasonId);
    setCreatingItem({ type: 'video', parentId: seasonId });
    setVideoForm({ 
      title: "", description: "", thumbnail: "", video_url: "", platform: "youtube", 
      season_id: seasonId, order: seasonVideos.length, duration: "", view_sources: [] 
    });
    setExpandedSeasons(prev => new Set(prev).add(seasonId));
  };

  // Start editing
  const startEditShow = (show) => {
    resetForms();
    setEditingItem({ type: 'show', data: show });
    setShowForm({ title: show.title, description: show.description || "", thumbnail: show.thumbnail || "", order: show.order || 0 });
  };

  const startEditSeason = (season) => {
    resetForms();
    setEditingItem({ type: 'season', data: season });
    setSeasonForm({ 
      title: season.title, 
      description: season.description || "", 
      thumbnail: season.thumbnail || "", 
      show_id: season.show_id,
      color: season.color || "#FFE500", 
      order: season.order || 0 
    });
  };

  const startEditVideo = (video) => {
    resetForms();
    setEditingItem({ type: 'video', data: video });
    setVideoForm({
      title: video.title,
      description: video.description || "",
      thumbnail: video.thumbnail || "",
      video_url: video.video_url,
      platform: video.platform,
      season_id: video.season_id,
      order: video.order || 0,
      duration: video.duration || "",
      view_sources: video.view_sources || []
    });
    // Cargar datos de vistas
    setViewsData({
      total_views: video.primary_cached_views || 0,
      views_last_updated: video.views_last_updated
    });
  };

  // Refresh views for current video
  const handleRefreshViews = async () => {
    if (!editingItem?.data?.id) return;
    
    setRefreshingViews(true);
    try {
      const response = await adminApi.post(`/metrics/video/${editingItem.data.id}/refresh`);
      setViewsData({
        total_views: response.data.total_views,
        views_last_updated: response.data.updated_at
      });
      // Actualizar view_sources con los nuevos cached_views
      if (response.data.sources) {
        const updatedSources = videoForm.view_sources.map((src, idx) => {
          const freshSource = response.data.sources.find(
            s => !s.is_primary && s.platform === src.platform && s.url === src.url
          );
          return freshSource ? { ...src, cached_views: freshSource.views } : src;
        });
        setVideoForm({ ...videoForm, view_sources: updatedSources });
      }
      toast.success(`Vistas actualizadas: ${formatViews(response.data.total_views)}`);
      onRefresh(); // Refrescar lista para mostrar nuevos valores
    } catch (error) {
      console.error("Error refreshing views:", error);
      toast.error("Error al actualizar vistas. La API puede estar limitada.");
    } finally {
      setRefreshingViews(false);
    }
  };

  // Refresh ALL videos views (global)
  const [refreshingAllViews, setRefreshingAllViews] = useState(false);
  const handleRefreshAllViews = async () => {
    setRefreshingAllViews(true);
    try {
      const response = await adminApi.post(`/metrics/refresh-all?limit=500`);
      toast.success(`Vistas actualizadas: ${response.data.updated} videos, ${response.data.errors} errores`);
      onRefresh();
    } catch (error) {
      console.error("Error refreshing all views:", error);
      toast.error("Error al actualizar vistas globales.");
    } finally {
      setRefreshingAllViews(false);
    }
  };

  // Submit handlers
  const handleShowSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem?.type === 'show') {
        await adminApi.put(`/shows/${editingItem.data.id}`, showForm);
        toast.success("Show actualizado");
      } else {
        await adminApi.post(`/shows`, showForm);
        toast.success("Show creado");
      }
      resetForms();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar show");
    }
  };

  const handleSeasonSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem?.type === 'season') {
        await adminApi.put(`/seasons/${editingItem.data.id}`, seasonForm);
        toast.success("Temporada actualizada");
      } else {
        await adminApi.post(`/seasons`, seasonForm);
        toast.success("Temporada creada");
      }
      resetForms();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar temporada");
    }
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem?.type === 'video') {
        await adminApi.put(`/videos/${editingItem.data.id}`, videoForm);
        toast.success("Video actualizado");
      } else {
        await adminApi.post(`/videos`, videoForm);
        toast.success("Video creado");
      }
      resetForms();
      onRefresh();
    } catch (error) {
      toast.error("Error al guardar video");
    }
  };

  // Delete handlers
  const handleDeleteShow = async (showId) => {
    if (!confirm("¿Eliminar este show y todas sus temporadas y videos?")) return;
    try {
      await adminApi.delete(`/shows/${showId}`);
      toast.success("Show eliminado");
      onRefresh();
    } catch (error) {
      toast.error("Error al eliminar show");
    }
  };

  const handleDeleteSeason = async (seasonId) => {
    if (!confirm("¿Eliminar esta temporada y todos sus videos?")) return;
    try {
      await adminApi.delete(`/seasons/${seasonId}`);
      toast.success("Temporada eliminada");
      onRefresh();
    } catch (error) {
      toast.error("Error al eliminar temporada");
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm("¿Eliminar este video?")) return;
    try {
      await adminApi.delete(`/videos/${videoId}`);
      toast.success("Video eliminado");
      onRefresh();
    } catch (error) {
      toast.error("Error al eliminar video");
    }
  };

  // Determine what form to show
  const isEditingShow = editingItem?.type === 'show';
  const isEditingSeason = editingItem?.type === 'season';
  const isEditingVideo = editingItem?.type === 'video';
  const isCreatingShow = creatingItem?.type === 'show';
  const isCreatingSeason = creatingItem?.type === 'season';
  const isCreatingVideo = creatingItem?.type === 'video';
  const showShowForm = isEditingShow || isCreatingShow;
  const showSeasonForm = isEditingSeason || isCreatingSeason;
  const showVideoForm = isEditingVideo || isCreatingVideo;
  const hasActiveForm = showShowForm || showSeasonForm || showVideoForm;

  // Form content component to avoid duplication
  const FormContent = () => (
    <div className="p-4 md:p-5">
      {/* Show Form */}
      {showShowForm && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-black flex items-center gap-2">
              <Tv size={20} />
              {isCreatingShow ? "Nuevo Show" : "Editar Show"}
            </h3>
            <Button variant="ghost" size="sm" onClick={resetForms} className="h-8 w-8 p-0">
              <X size={16} />
            </Button>
          </div>
          <form onSubmit={handleShowSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Título *</label>
              <Input
                value={showForm.title}
                onChange={(e) => setShowForm({...showForm, title: e.target.value})}
                required
                placeholder="Nombre del show"
                className="bg-gray-50 border-gray-200"
                data-testid="show-title-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Descripción</label>
              <Input
                value={showForm.description}
                onChange={(e) => setShowForm({...showForm, description: e.target.value})}
                placeholder="Descripción del show"
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Thumbnail</label>
              <ImageUploader
                value={showForm.thumbnail}
                onChange={(url) => setShowForm({...showForm, thumbnail: url})}
                category="thumbnails"
                recommendedSize="400x600 px (vertical)"
              />
            </div>
            <div className="flex gap-2 pt-2 pb-4 lg:pb-0">
              <Button type="submit" className="flex-1 bg-black text-white rounded-full h-12 lg:h-10 text-base">
                <Save size={16} className="mr-2" />
                {isCreatingShow ? "Crear" : "Guardar"}
              </Button>
            </div>
          </form>
        </>
      )}

      {/* Season Form */}
      {showSeasonForm && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-black flex items-center gap-2">
              <Folder size={20} />
              {isCreatingSeason ? "Nueva Temporada" : "Editar Temporada"}
            </h3>
            <Button variant="ghost" size="sm" onClick={resetForms} className="h-8 w-8 p-0">
              <X size={16} />
            </Button>
          </div>
          <form onSubmit={handleSeasonSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Título *</label>
              <Input
                value={seasonForm.title}
                onChange={(e) => setSeasonForm({...seasonForm, title: e.target.value})}
                required
                placeholder="Ej: Temporada 1"
                className="bg-gray-50 border-gray-200"
                data-testid="season-title-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Descripción</label>
              <Input
                value={seasonForm.description}
                onChange={(e) => setSeasonForm({...seasonForm, description: e.target.value})}
                placeholder="Descripción"
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium flex items-center gap-1">
                <Palette size={14} /> Color
              </label>
              <div 
                className="h-10 rounded-lg mb-2 flex items-center justify-center font-bold text-sm"
                style={{ backgroundColor: seasonForm.color, color: getContrastColor(seasonForm.color) }}
              >
                {seasonForm.title || "Vista previa"}
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setSeasonForm({...seasonForm, color: c.value})}
                    className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                      seasonForm.color === c.value ? 'ring-2 ring-offset-1 ring-black scale-110' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: c.value }}
                    title={c.name}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Thumbnail</label>
              <ImageUploader
                value={seasonForm.thumbnail}
                onChange={(url) => setSeasonForm({...seasonForm, thumbnail: url})}
                category="thumbnails"
                recommendedSize="400x600 px"
              />
            </div>
            <div className="flex gap-2 pt-2 pb-4 lg:pb-0">
              <Button type="submit" className="flex-1 bg-black text-white rounded-full h-12 lg:h-10 text-base">
                <Save size={16} className="mr-2" />
                {isCreatingSeason ? "Crear" : "Guardar"}
              </Button>
            </div>
          </form>
        </>
      )}

      {/* Video Form */}
      {showVideoForm && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-black flex items-center gap-2">
              <Film size={20} />
              {isCreatingVideo ? "Nuevo Episodio" : "Editar Episodio"}
            </h3>
            <Button variant="ghost" size="sm" onClick={resetForms} className="h-8 w-8 p-0">
              <X size={16} />
            </Button>
          </div>
          <form onSubmit={handleVideoSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Título *</label>
              <Input
                value={videoForm.title}
                onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                required
                placeholder="Título del episodio"
                className="bg-gray-50 border-gray-200"
                data-testid="video-title-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Descripción</label>
              <Input
                value={videoForm.description}
                onChange={(e) => setVideoForm({...videoForm, description: e.target.value})}
                placeholder="Descripción"
                className="bg-gray-50 border-gray-200"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">URL del Video *</label>
              <Input
                value={videoForm.video_url}
                onChange={(e) => setVideoForm({...videoForm, video_url: e.target.value})}
                required
                placeholder="https://youtube.com/watch?v=..."
                className="bg-gray-50 border-gray-200"
                data-testid="video-url-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1 font-medium">Plataforma</label>
                <Select
                  value={videoForm.platform}
                  onValueChange={(v) => setVideoForm({...videoForm, platform: v})}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="vimeo">Vimeo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1 font-medium">Duración</label>
                <Input
                  value={videoForm.duration}
                  onChange={(e) => setVideoForm({...videoForm, duration: e.target.value})}
                  placeholder="10:30"
                  className="bg-gray-50 border-gray-200"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1 font-medium">Thumbnail (opcional)</label>
              <ImageUploader
                value={videoForm.thumbnail}
                onChange={(url) => setVideoForm({...videoForm, thumbnail: url})}
                category="thumbnails"
                recommendedSize="Se usa miniatura de YouTube si está vacío"
              />
            </div>

            {/* Métricas Unificadas - URLs secundarios para contar views */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm text-gray-600 font-medium flex items-center gap-2">
                  <BarChart3 size={16} className="text-[#E91E8C]" />
                  Métricas Unificadas
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setVideoForm({
                    ...videoForm,
                    view_sources: [...videoForm.view_sources, { platform: "youtube", url: "", cached_views: 0 }]
                  })}
                  className="h-7 text-xs"
                  data-testid="add-view-source-btn"
                >
                  <Plus size={12} className="mr-1" />
                  Agregar fuente
                </Button>
              </div>
              
              {/* Stats y botón de actualizar - solo si estamos editando */}
              {isEditingVideo && (
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg mb-3 border border-pink-100">
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <p className="text-xl font-bold text-[#E91E8C]">
                        {formatViews(viewsData?.total_views || editingItem?.data?.primary_cached_views || 0)}
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase">Vistas totales</p>
                    </div>
                    <div className="h-8 w-px bg-gray-200" />
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock size={12} />
                      <span>{formatRelativeTime(viewsData?.views_last_updated || editingItem?.data?.views_last_updated)}</span>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshViews}
                    disabled={refreshingViews}
                    className="h-8 text-xs border-[#E91E8C] text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white"
                    data-testid="refresh-views-btn"
                  >
                    <RefreshCw size={12} className={`mr-1 ${refreshingViews ? 'animate-spin' : ''}`} />
                    {refreshingViews ? 'Actualizando...' : 'Actualizar'}
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mb-3">
                Agrega URLs de otras plataformas (YouTube, Facebook, TikTok) para sumar sus vistas al total.
              </p>
              
              {videoForm.view_sources.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <Eye size={20} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-xs text-gray-500">Sin fuentes adicionales</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {videoForm.view_sources.map((source, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className="w-16">
                        <Select
                          value={source.platform}
                          onValueChange={(v) => {
                            const newSources = [...videoForm.view_sources];
                            newSources[index] = { ...newSources[index], platform: v };
                            setVideoForm({ ...videoForm, view_sources: newSources });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="youtube">
                              <span className="flex items-center gap-1">
                                {PLATFORM_ICONS.youtube} YT
                              </span>
                            </SelectItem>
                            <SelectItem value="facebook">
                              <span className="flex items-center gap-1">
                                {PLATFORM_ICONS.facebook} FB
                              </span>
                            </SelectItem>
                            <SelectItem value="tiktok">
                              <span className="flex items-center gap-1">
                                {PLATFORM_ICONS.tiktok} TT
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Input
                        value={source.url}
                        onChange={(e) => {
                          const newSources = [...videoForm.view_sources];
                          newSources[index] = { ...newSources[index], url: e.target.value };
                          setVideoForm({ ...videoForm, view_sources: newSources });
                        }}
                        placeholder={
                          source.platform === "youtube" ? "https://youtube.com/watch?v=..." :
                          source.platform === "facebook" ? "https://facebook.com/watch/?v=..." :
                          "https://tiktok.com/@user/video/..."
                        }
                        className="flex-1 h-8 text-xs bg-white"
                        data-testid={`view-source-url-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newSources = videoForm.view_sources.filter((_, i) => i !== index);
                          setVideoForm({ ...videoForm, view_sources: newSources });
                        }}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-2 pb-4 lg:pb-0">
              <Button type="submit" className="flex-1 bg-black text-white rounded-full h-12 lg:h-10 text-base">
                <Save size={16} className="mr-2" />
                {isCreatingVideo ? "Crear" : "Guardar"}
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );

  return (
    <div className="w-full max-w-full lg:grid lg:grid-cols-5 lg:gap-6">
      {/* Main Content - Tree View */}
      <div className="w-full max-w-full lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-4 md:p-5 shadow-sm" data-testid="content-tree">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h2 className="text-base md:text-xl font-bold text-black flex items-center gap-2">
            <Tv size={18} className="md:w-[22px] md:h-[22px] flex-shrink-0" />
            Contenido
          </h2>
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleRefreshAllViews}
              disabled={refreshingAllViews}
              variant="outline"
              className="text-xs px-3 py-1.5 border-[#E91E8C] text-[#E91E8C] hover:bg-[#E91E8C] hover:text-white flex-shrink-0"
              data-testid="refresh-all-views-btn"
            >
              <RefreshCw size={14} className={`mr-1 ${refreshingAllViews ? 'animate-spin' : ''}`} />
              {refreshingAllViews ? 'Actualizando...' : 'Actualizar Views'}
            </Button>
            <Button 
              onClick={startCreateShow} 
              className="bg-black text-white rounded-full text-xs md:text-base px-3 py-1.5 md:px-4 md:py-2 flex-shrink-0"
              data-testid="add-show-btn"
            >
              <Plus size={14} className="mr-1 md:w-4 md:h-4" />
              Show
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar..."
            className="pl-9 bg-gray-50 border-gray-200 text-base"
            data-testid="content-search"
          />
        </div>

        {/* Tree */}
        <div className="space-y-1 overflow-y-auto overflow-x-hidden max-h-[50vh] md:max-h-[60vh] lg:max-h-[600px]">
          {filteredShows.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              {shows.length === 0 ? "No hay shows. Crea uno para comenzar." : "No se encontraron resultados."}
            </p>
          ) : (
            filteredShows.map((show) => {
              const showSeasons = getSeasonsByShow(show.id);
              const isExpanded = expandedShows.has(show.id);
              const isActive = editingItem?.type === 'show' && editingItem?.data?.id === show.id;
              const isDragging = draggedItem?.type === 'show' && draggedItem?.id === show.id;
              const isDragOver = dragOverItem?.type === 'show' && dragOverItem?.id === show.id;

              return (
                <div key={show.id} className="select-none">
                  {/* Show Row */}
                  <div 
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'show', show.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, 'show', show.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, 'show', show.id)}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                      isActive ? 'bg-pink-100 border border-[#E91E8C]' : 
                      isDragOver ? 'bg-blue-100 border-2 border-blue-400 border-dashed' :
                      isDragging ? 'opacity-50' :
                      'hover:bg-gray-100'
                    }`}
                    data-testid={`show-row-${show.id}`}
                  >
                    <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                      <GripVertical size={16} />
                    </div>
                    <button 
                      onClick={() => toggleShow(show.id)}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {show.thumbnail && (
                      <img src={show.thumbnail} alt="" className="w-8 h-12 object-cover rounded" />
                    )}
                    <div className="flex-1 min-w-0" onClick={() => toggleShow(show.id)}>
                      <p className="font-semibold text-black truncate">{show.title}</p>
                      <p className="text-xs text-gray-500">{showSeasons.length} temporadas</p>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 hover:opacity-100">
                      <Button size="sm" variant="ghost" onClick={() => startCreateSeason(show.id)} className="h-7 w-7 p-0" title="Agregar temporada">
                        <Plus size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => startEditShow(show)} className="h-7 w-7 p-0">
                        <Edit2 size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteShow(show.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-600">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>

                  {/* Seasons */}
                  {isExpanded && (
                    <div className="ml-6 border-l-2 border-gray-200 pl-2 mt-1 space-y-1">
                      {showSeasons.map((season) => {
                        const seasonVideos = getVideosBySeason(season.id);
                        const isSeasonExpanded = expandedSeasons.has(season.id);
                        const isSeasonActive = editingItem?.type === 'season' && editingItem?.data?.id === season.id;
                        const isSeasonDragging = draggedItem?.type === 'season' && draggedItem?.id === season.id;
                        const isSeasonDragOver = dragOverItem?.type === 'season' && dragOverItem?.id === season.id && dragOverItem?.parentId === show.id;

                        return (
                          <div key={season.id}>
                            {/* Season Row */}
                            <div 
                              draggable
                              onDragStart={(e) => handleDragStart(e, 'season', season.id, show.id)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => handleDragOver(e, 'season', season.id, show.id)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, 'season', season.id, show.id)}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                                isSeasonActive ? 'bg-pink-100 border border-[#E91E8C]' : 
                                isSeasonDragOver ? 'bg-blue-100 border-2 border-blue-400 border-dashed' :
                                isSeasonDragging ? 'opacity-50' :
                                'hover:bg-gray-100'
                              }`}
                              data-testid={`season-row-${season.id}`}
                            >
                              <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                                <GripVertical size={14} />
                              </div>
                              <button 
                                onClick={() => toggleSeason(season.id)}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                {isSeasonExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </button>
                              <div 
                                className="w-2 h-6 rounded-full flex-shrink-0"
                                style={{ backgroundColor: season.color || "#FFE500" }}
                              />
                              <Folder size={16} className="text-gray-400 flex-shrink-0" />
                              <div className="flex-1 min-w-0" onClick={() => toggleSeason(season.id)}>
                                <p className="font-medium text-black text-sm truncate">{season.title}</p>
                                <p className="text-xs text-gray-500">{seasonVideos.length} episodios</p>
                              </div>
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" onClick={() => startCreateVideo(season.id)} className="h-6 w-6 p-0" title="Agregar episodio">
                                  <Plus size={12} />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => startEditSeason(season)} className="h-6 w-6 p-0">
                                  <Edit2 size={12} />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteSeason(season.id)} className="h-6 w-6 p-0 text-red-500 hover:text-red-600">
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            </div>

                            {/* Videos */}
                            {isSeasonExpanded && (
                              <div className="ml-6 border-l-2 border-gray-100 pl-2 mt-1 space-y-1">
                                {seasonVideos.map((video) => {
                                  const isVideoActive = editingItem?.type === 'video' && editingItem?.data?.id === video.id;
                                  const isVideoDragging = draggedItem?.type === 'video' && draggedItem?.id === video.id;
                                  const isVideoDragOver = dragOverItem?.type === 'video' && dragOverItem?.id === video.id && dragOverItem?.parentId === season.id;
                                  return (
                                    <div 
                                      key={video.id}
                                      draggable
                                      onDragStart={(e) => handleDragStart(e, 'video', video.id, season.id)}
                                      onDragEnd={handleDragEnd}
                                      onDragOver={(e) => handleDragOver(e, 'video', video.id, season.id)}
                                      onDragLeave={handleDragLeave}
                                      onDrop={(e) => handleDrop(e, 'video', video.id, season.id)}
                                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                                        isVideoActive ? 'bg-pink-100 border border-[#E91E8C]' : 
                                        isVideoDragOver ? 'bg-blue-100 border-2 border-blue-400 border-dashed' :
                                        isVideoDragging ? 'opacity-50' :
                                        'hover:bg-gray-50'
                                      }`}
                                      onClick={() => startEditVideo(video)}
                                      data-testid={`video-row-${video.id}`}
                                    >
                                      <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                                        <GripVertical size={12} />
                                      </div>
                                      <Film size={14} className="text-gray-400 flex-shrink-0" />
                                      {video.thumbnail && (
                                        <img src={video.thumbnail} alt="" className="w-10 h-6 object-cover rounded" />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm text-black truncate">{video.title}</p>
                                        {video.duration && <p className="text-xs text-gray-400">{video.duration}</p>}
                                      </div>
                                      <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); startEditVideo(video); }} className="h-6 w-6 p-0">
                                          <Edit2 size={12} />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteVideo(video.id); }} className="h-6 w-6 p-0 text-red-500 hover:text-red-600">
                                          <Trash2 size={12} />
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                                {seasonVideos.length === 0 && (
                                  <p className="text-xs text-gray-400 py-2 pl-2">Sin episodios</p>
                                )}
                                {/* Add Episode Button */}
                                <button
                                  onClick={() => startCreateVideo(season.id)}
                                  className="flex items-center gap-2 p-2 text-sm text-gray-500 hover:text-black hover:bg-gray-50 rounded-lg w-full"
                                  data-testid={`add-video-to-${season.id}`}
                                >
                                  <Plus size={14} />
                                  Agregar episodio
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {showSeasons.length === 0 && (
                        <p className="text-xs text-gray-400 py-2 pl-2">Sin temporadas</p>
                      )}
                      {/* Add Season Button */}
                      <button
                        onClick={() => startCreateSeason(show.id)}
                        className="flex items-center gap-2 p-2 text-sm text-gray-500 hover:text-black hover:bg-gray-50 rounded-lg w-full"
                        data-testid={`add-season-to-${show.id}`}
                      >
                        <Plus size={14} />
                        Agregar temporada
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Desktop: Form Panel al lado */}
      <div className="hidden lg:block lg:col-span-2">
        {!hasActiveForm ? (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center sticky top-4">
            <Tv size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Selecciona un elemento para editar o crea uno nuevo</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm sticky top-4">
            <FormContent />
          </div>
        )}
      </div>

      {/* Mobile: Drawer desde abajo */}
      {hasActiveForm && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={resetForms}
          />
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto lg:hidden">
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>
            <FormContent />
          </div>
        </>
      )}
    </div>
  );
}
